
import Button from '../../../components/base/Button';

export default function PartnershipsSection() {
  const partnerships = [
    {
      title: 'Property Owners',
      description: 'Maximize your rental income with our professional management services and furnished rental expertise.',
      icon: 'ri-key-2-line',
      benefits: ['Higher rental yields', 'Professional management', 'Stress-free operations', 'Market expertise'],
      image: 'https://public.readdy.ai/ai/img_res/055e7f68a65189bb32b9ace2361d488b.jpg'
    },
    {
      title: 'Real Estate Investors',
      description: 'Scale your portfolio with DSCR loans and expert guidance on profitable furnished rental investments.',
      icon: 'ri-line-chart-line',
      benefits: ['DSCR loan access', 'Investment strategies', 'Portfolio scaling', 'Market analysis'],
      image: 'https://readdy.ai/api/search-image?query=Professional%20real%20estate%20investment%20meeting%20with%202%20females%20and%201%20male%20aged%2035-45%20years%20old%20wearing%20casual%20business%20attire%20without%20coats%2C%20relaxed%20polo%20shirts%20and%20casual%20pants%2C%202%20white%20professionals%20and%201%20Indian%20professional%2C%20contemporary%20conference%20room%20with%20investment%20charts%20on%20screens%2C%20friendly%20professional%20consultation%20meeting%2C%20bright%20natural%20lighting%2C%20modern%20office%20setting%20with%20property%20investment%20documents%2C%20collaborative%20atmosphere%2C%20focused%20group%20discussion%2C%20approachable%20business%20casual%20dress%20code&width=600&height=400&seq=partnership-investors-2f1m-35-45&orientation=landscape'
    },
    {
      title: 'Corporate Partners',
      description: 'Partner with us to provide quality accommodations for your employees and business travelers.',
      icon: 'ri-building-2-line',
      benefits: ['Corporate rates', 'Flexible terms', 'Quality assurance', 'Dedicated support'],
      image: 'https://readdy.ai/api/search-image?query=Professional%20white%20businessman%20in%20his%20late%2030s%20standing%20confidently%20in%20front%20of%20classic%20New%20York%20City%20brownstone%20building%2C%20wearing%20casual%20business%20attire%2C%20holding%20modern%20black%20rolling%20suitcase%20and%20laptop%20bag%2C%20urban%20residential%20street%20setting%20with%20typical%20NYC%20architecture%2C%20bright%20natural%20daylight%2C%20business%20traveler%20ready%20for%20corporate%20accommodation%2C%20well-maintained%20historic%20brownstone%20facade%2C%20city%20professional%20lifestyle&width=600&height=400&seq=partnership-corporate-nyc-brownstone&orientation=landscape'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-slate-50 to-white relative overflow-hidden">
      {/* Modern background elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-100/30 to-slate-100/30 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-1 w-80 h-80 bg-gradient-to-tr from-slate-100/40 to-blue-50/40 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-20">
          <div className="inline-flex items-center px-6 py-3 bg-white shadow-lg rounded-full text-slate-700 text-sm font-semibold mb-8 border border-slate-200">
            <i className="ri-handshake-line mr-2 text-blue-600"></i>
            Strategic Partnerships
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
            Partner With Us
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Join our network of successful partners and grow your business with our proven furnished rental solutions and expert guidance.
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {partnerships.map((partnership, index) => (
            <div key={index} className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-slate-100 overflow-hidden">
              {/* Modern image header */}
              <div className="relative h-56 overflow-hidden">
                <div 
                  className="absolute inset-0 bg-cover bg-center transform group-hover:scale-110 transition-transform duration-700" 
                  style={{ backgroundImage: `url('${partnership.image}')` }}
                ></div>
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-slate-900/20 to-transparent"></div>
                
                {/* Floating icon */}
                <div className="absolute bottom-4 left-6 w-14 h-14 bg-white/95 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
                  <i className={`${partnership.icon} text-2xl text-blue-600`}></i>
                </div>
              </div>
              
              <div className="p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                  {partnership.title}
                </h3>
                
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {partnership.description}
                </p>
                
                {/* Modern benefits list */}
                <div className="space-y-3 mb-8">
                  {partnership.benefits.map((benefit, benefitIndex) => (
                    <div key={benefitIndex} className="flex items-center">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <i className="ri-check-line text-green-600 text-sm"></i>
                      </div>
                      <span className="text-slate-700 font-medium">{benefit}</span>
                    </div>
                  ))}
                </div>
                
                <Button variant="outline" className="w-full group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-all duration-300">
                  <i className="ri-arrow-right-line mr-2"></i>
                  Learn More
                </Button>
              </div>
              
              {/* Modern hover effect */}
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-blue-200 rounded-2xl transition-all duration-300"></div>
            </div>
          ))}
        </div>
        
        {/* Modern CTA section */}
        <div className="text-center bg-white rounded-3xl shadow-xl p-12 border border-slate-100">
          <div className="max-w-2xl mx-auto">
            <h3 className="text-3xl font-bold text-slate-900 mb-4">
              Ready to Partner With Us?
            </h3>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              Join hundreds of successful partners who have grown their business with

 our proven strategies and dedicated support.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                <i className="ri-handshake-line mr-2"></i>
                Become a Partner
              </Button>
              <Button size="lg" variant="outline">
                <i className="ri-phone-line mr-2"></i>
                Schedule a Call
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
